import React, { useState } from 'react';
import { Check, Loader2, PartyPopper } from 'lucide-react';

const Cta = () => {
    const [result, setResult] = useState("");
    const [status, setStatus] = useState("idle"); // idle, loading, success, error

    const onSubmit = async (event) => {
        event.preventDefault();
        setStatus("loading");
        setResult("Sending message...");
        
        const formData = new FormData(event.target);
        // Using the updated Access Key provided by the user
        formData.append("access_key", "d9d6f515-6909-420c-8093-d2c8d5041e34");

        try {
            const response = await fetch("https://api.web3forms.com/submit", {
                method: "POST",
                body: formData
            });

            const data = await response.json();

            if (data.success) {
                setStatus("success");
                setResult("Message sent successfully! We'll be in touch soon.");
            } else {
                console.log("Web3Forms Error", data);
                setStatus("error");
                setResult(data.message || "Something went wrong. Please try again.");
            }
        } catch (error) {
            console.log("Web3Forms Fetch Error", error);
            setStatus("error");
            setResult("Network error. Please try again later.");
        }
    };

    return (
        <section className="relative section-padding-lg lg:min-h-screen flex flex-col justify-center overflow-hidden bg-motion-dark" id="contact">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
                <div className="text-center mb-10" data-aos="fade-up">
                    <h2 className="heading-xl text-white mb-6">
                        Talk to an expert
                    </h2>
                    <p className="paragraph-lg text-white/60 max-w-2xl mx-auto">
                        Schedule a call with one of our experts to learn more about what Trustpoint can do for your business.
                    </p>
                </div>

                <div className="relative min-h-[400px] flex items-center justify-center">
                    {status === "success" ? (
                        <div 
                            className="bg-white/5 backdrop-blur-sm border border-accent/20 rounded-[2.5rem] p-12 md:p-16 text-center w-full max-w-2xl animate-in fade-in zoom-in duration-500"
                            data-aos="zoom-in"
                        >
                            <div className="w-20 h-20 bg-accent rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg shadow-accent/20">
                                <Check className="w-10 h-10 text-white" strokeWidth="3" />
                            </div>
                            <h3 className="heading-lg text-white mb-4">Message Sent!</h3>
                            <p className="paragraph-lg text-white/60 mb-8">
                                Thank you for reaching out. A Trustpoint specialist will review your request and contact you shortly.
                            </p>
                            <button 
                                onClick={() => setStatus("idle")}
                                className="text-accent hover:underline font-medium"
                            >
                                Send another message
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={onSubmit} className={`space-y-4 w-full transition-all duration-500 ${status === "loading" ? "opacity-50 pointer-events-none" : "opacity-100"}`} data-aos="fade-up" data-aos-delay="200">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-white text-sm font-light mb-2">First Name *</label>
                                    <input type="text" name="first_name" required className="form-input" />
                                </div>
                                <div>
                                    <label className="block text-white text-sm font-light mb-2">Last Name *</label>
                                    <input type="text" name="last_name" required className="form-input" />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-white text-sm font-light mb-2">Email *</label>
                                    <input type="email" name="email" required className="form-input" />
                                </div>
                                <div>
                                    <label className="block text-white text-sm font-light mb-2">Phone *</label>
                                    <input type="tel" name="phone" required className="form-input" />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-white text-sm font-light mb-2">Job Title *</label>
                                    <input type="text" name="job_title" required className="form-input" />
                                </div>
                                <div>
                                    <label className="block text-white text-sm font-light mb-2">Company Name *</label>
                                    <input type="text" name="company_name" required className="form-input" />
                                </div>
                            </div>

                            <div>
                                <label className="block text-white text-sm font-light mb-2">Company HQ State *</label>
                                <select name="state" required className="form-input appearance-none cursor-pointer">
                                    <option value="">Please Select</option>
                                    <option value="AL">Alabama</option>
                                    <option value="AK">Alaska</option>
                                    <option value="AZ">Arizona</option>
                                    <option value="CA">California</option>
                                    <option value="CO">Colorado</option>
                                    <option value="CT">Connecticut</option>
                                    <option value="DE">Delaware</option>
                                    <option value="FL">Florida</option>
                                    <option value="GA">Georgia</option>
                                    <option value="HI">Hawaii</option>
                                    <option value="ID">Idaho</option>
                                    <option value="IL">Illinois</option>
                                    <option value="IN">Indiana</option>
                                    <option value="IA">Iowa</option>
                                    <option value="KS">Kansas</option>
                                    <option value="KY">Kentucky</option>
                                    <option value="LA">Louisiana</option>
                                    <option value="ME">Maine</option>
                                    <option value="MD">Maryland</option>
                                    <option value="MA">Massachusetts</option>
                                    <option value="MI">Michigan</option>
                                    <option value="MN">Minnesota</option>
                                    <option value="MS">Mississippi</option>
                                    <option value="MO">Missouri</option>
                                    <option value="MT">Montana</option>
                                    <option value="NE">Nebraska</option>
                                    <option value="NV">Nevada</option>
                                    <option value="NH">New Hampshire</option>
                                    <option value="NJ">New Jersey</option>
                                    <option value="NM">New Mexico</option>
                                    <option value="NY">New York</option>
                                    <option value="NC">North Carolina</option>
                                    <option value="ND">North Dakota</option>
                                    <option value="OH">Ohio</option>
                                    <option value="OK">Oklahoma</option>
                                    <option value="OR">Oregon</option>
                                    <option value="PA">Pennsylvania</option>
                                    <option value="RI">Rhode Island</option>
                                    <option value="SC">South Carolina</option>
                                    <option value="SD">South Dakota</option>
                                    <option value="TN">Tennessee</option>
                                    <option value="TX">Texas</option>
                                    <option value="UT">Utah</option>
                                    <option value="VT">Vermont</option>
                                    <option value="VA">Virginia</option>
                                    <option value="WA">Washington</option>
                                    <option value="WV">West Virginia</option>
                                    <option value="WI">Wisconsin</option>
                                    <option value="WY">Wyoming</option>
                                </select>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-white text-sm font-light mb-1">Product interest *</label>
                                    <p className="text-white/40 text-sm mb-2">select all that apply</p>
                                    <div className="space-y-2">
                                        {['Revenue Cycle Management', 'Real-Time Claim Tracking', 'Denial Monitoring', 'Revenue Projections', 'Free Financial Audit', 'Other'].map((item, i) => (
                                            <label key={i} className="flex items-center gap-3 cursor-pointer group">
                                                <div className="relative w-5 h-5 sm:w-6 sm:h-6">
                                                    <input type="checkbox" name="interest[]" value={item} className="sr-only peer" />
                                                    <div className="absolute inset-0 rounded border border-primary-light/20 bg-primary-light/5 peer-checked:bg-accent peer-checked:border-accent transition-all"></div>
                                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                                        <div className="w-[5px] h-[9px] border-r-2 border-b-2 border-white rotate-45 -mt-1 opacity-0 peer-checked:opacity-100 transition-opacity"></div>
                                                    </div>
                                                </div>
                                                <span className="text-sm text-white/80 group-hover:text-white transition-colors">{item}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-white text-sm font-light mb-4">Type of Organization *</label>
                                    <div className="space-y-2">
                                        {['Below 30 Providers', 'Above 30 Providers', 'Other'].map((item, i) => (
                                            <label key={i} className="flex items-center gap-3 cursor-pointer group">
                                                <div className="relative w-5 h-5 sm:w-6 sm:h-6">
                                                    <input type="radio" name="org_type" value={item} className="sr-only peer" />
                                                    <div className="absolute inset-0 rounded-full border border-primary-light/20 bg-primary-light/5 peer-checked:border-accent peer-checked:bg-accent transition-all"></div>
                                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                                        <div className="w-2.5 h-2.5 rounded-full bg-white opacity-0 peer-checked:opacity-100 transition-opacity"></div>
                                                    </div>
                                                </div>
                                                <span className="text-sm text-white/80 group-hover:text-white transition-colors">{item}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-white text-sm font-light mb-2">Message</label>
                                <textarea name="message" rows="2" className="form-input resize-none"></textarea>
                            </div>

                            <div className="flex items-start justify-center gap-4 max-w-2xl mx-auto">
                                <input type="checkbox" name="marketing_consent" id="consent" required className="hidden peer" />
                                <label htmlFor="consent" className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 shrink-0 rounded border border-primary-light/20 bg-primary-light/5 flex items-center justify-center peer-checked:bg-accent peer-checked:border-accent transition-all cursor-pointer mt-0.5">
                                    <Check className="w-3 h-3 sm:w-3.5 sm:h-3.5 lg:w-4 lg:h-4 text-white opacity-0 peer-checked:opacity-100 transition-opacity" strokeWidth="3" />
                                </label>
                                <p className="text-sm text-white/60 font-light leading-relaxed text-left">
                                    By checking this box, you consent to receive marketing SMS messages from Trustpoint at the number
                                    provided. Msg&data rates may apply. Msg frequency varies. Unsubscribe at any time by replying STOP.
                                    Reply HELP for help. View our <button type="button" className="text-accent hover:underline">Privacy Policy</button>.
                                </p>
                            </div>

                            <div className="pt-4 text-center">
                                <p className="text-white/60 text-sm mb-4">
                                    Trustpoint is committed to protecting your <button type="button" className="text-accent hover:underline">privacy</button>.
                                </p>
                                
                                <div className="flex flex-col items-center gap-4">
                                    <button 
                                        type="submit" 
                                        disabled={status === "loading"}
                                        className={`btn-primary-lg px-16 min-w-[200px] flex items-center justify-center gap-2 ${status === "loading" ? "opacity-70 cursor-not-allowed" : ""}`}
                                    >
                                        {status === "loading" ? (
                                            <>
                                                <Loader2 className="w-5 h-5 animate-spin" />
                                                Sending...
                                            </>
                                        ) : "Submit"}
                                    </button>

                                    {status === "error" && result && (
                                        <p className="text-sm py-2 px-4 rounded-full transition-all bg-red-500/10 text-red-400">
                                            {result}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </form>
                    )}
                </div>
            </div>

            <div className="absolute inset-0 pointer-events-none overflow-hidden select-none">
                <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-accent/5 rounded-full blur-[60px] animate-blob will-change-transform"></div>
                <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-accent/5 rounded-full blur-[60px] animate-blob-reverse will-change-transform"></div>
            </div>
        </section>
    );
};

export default Cta;
